import '../style.css';
import {Feature, Map, Overlay, View} from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import {fromLonLat} from "ol/proj";
import VectorSource from "ol/source/Vector";
import CircleStyle from "ol/style/Circle";
import {Text, Fill, Stroke, Style, Icon} from "ol/style";
import {Cluster} from "ol/source";
import VectorLayer from "ol/layer/Vector";
import {Point} from "ol/geom";
import Geolocation from 'ol/Geolocation.js';

/**
 * Initialize the map
 *
 * @param elementId
 * @returns {Map} The OL map instance
 */
window.initializeWarchiveMap = function(elementId) {

  // Get the map element
  const map_element = document.querySelector("#"+elementId);
  const popup_container = map_element ? map_element.parentElement.querySelector('.warchive-map-popup') : null;
  const popup_content = popup_container ? popup_container.querySelector('.popup-content') : null;
  const popup_closer = popup_container ? popup_container.querySelector('.popup-closer') : null;

  // Set default values
  let center = fromLonLat([9.580078, 48.654007]); // Somewhere in the middle of Europe (in Germany)
  let zoom = 4;
  let selected_feature_id = null;
  let feature_types = [];
  let features = [];
  let features_top = [];
  let geolocation_enabled = false;
  let geolocation_has_triggered = false;

  // Get data attributes from the HTML element.
  if (map_element && map_element.dataset) {
    if (map_element.dataset.lat && map_element.dataset.lon) {
      center = fromLonLat([map_element.dataset.lon, map_element.dataset.lat]);
      zoom = 14;
    }
    if (map_element.dataset.featureSelectedId) {
      selected_feature_id = map_element.dataset.featureSelectedId;
    }
    if (map_element.dataset.featureTypes) {
      feature_types = map_element.dataset.featureTypes.split(',');
    }
    if (map_element.dataset.geolocation) {
      geolocation_enabled = true;
    }
  }

  // If article node is still a draft. Then add a placeholder feature to the map
  if (drupalSettings.node && drupalSettings.node.nodeIsDraft === true) {
    // Add a placeholder feature to the map
    const feature = new Feature({
      geometry: new Point(center),
      type: drupalSettings.node.nodeBundle,
    });
    feature.setId('draft');
    // Add feature to the map
    features_top.push(feature);
  }

  const overlay = new Overlay({
    element: popup_container,
    autoPan: {
      animation: {
        duration: 250,
      },
    },
  });

  const source = new VectorSource({
    features: features,
  });
  const sourceTop = new VectorSource({
    features: features_top,
  });

  const clusterSource = new Cluster({
    distance: 30,
    minDistance: 28, // The min distance cluster icons need to be seperated from each other
    source: source,
  });

  // This contains all features with clustering. This shown when zoomed out.
  const styleCache = {};
  const clusters = new VectorLayer({
    minResolution: 14,
    source: clusterSource,
    style: function (feature) {
      return clusterStyle(feature);
    },
  });

  const raster = new TileLayer({
    source: new OSM(),
  });

  // This contains all features without clustering. This is only shown when zoomed in very far.
  const vectorLayer = new VectorLayer({
    source: source,
    minResolution: 0,
    maxResolution: 14,
    style: function (feature) {
      return featureStyle(feature);
    },
  });
  vectorLayer.setZIndex(500);

  const vectorLayerTop = new VectorLayer({
    source: sourceTop,
    style: function (feature) {
      return featureStyle(feature);
    },
  });
  vectorLayerTop.setZIndex(1000);

  const view = new View({
    center: center,
    zoom: zoom,
    resolutions: [32768, 16384, 8192, 4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5, 0.25],
    constrainResolution: true,
  })


  const mapElement = document.getElementById(elementId);
  if (!mapElement) {
    console.error(`Element with ID ${elementId} not found.`);
    return null;
  }

  const mapInstance = new Map({
    target: elementId,
    layers: [raster, vectorLayer, clusters, vectorLayerTop],
    overlays: [overlay],
    view: view,
  });

  /**
   * Show popup when clicking a feature
   */
  mapInstance.on('click', async function (evt) {
    let feature = mapInstance.forEachFeatureAtPixel(evt.pixel, function (feature) {
      return feature;
    });
    if (feature) {
      // Check if the clicked feature is a cluster source feature
      const isClusterFeature = feature.get('features');
      if (isClusterFeature) {
        if (isClusterFeature.length > 1) {
          return; // Don't show the popup
        }
        // Get the single feature inside the cluster source
        feature = feature.get('features')[0];
      }

      // Don't create popup on draft feature
      if (feature.getId() === 'draft') {
        return;
      }

      // Show the popup
      const coordinates = feature.getGeometry().getCoordinates();
      // Set popup coordinates
      overlay.setPosition(coordinates);

      // Show loader
      popup_content.innerHTML = 'Loading..';

      // Load the data
      let html = await loadArticleHtml(feature.getId());
      // Set HTML of popup
      popup_content.innerHTML = html;
    }
  });

  // Load the features
  loadFeatures();

  /**
   * Load all features from the API and add them to the source.
   *
   * @returns {Promise<*[]>}
   */
  async function loadFeatures() {
    const response = await fetch("/api/warchive/map-features");
    // Show error when there is something wrong
    if (!response.ok) {
      console.log("Features could not be loaded. Error: [" + response.status + "] " + response.statusText, {timeout: false});
      return [];
    }
    const data = await response.json();
    const count = data.length;

    // Add features to an array
    for (let i = 0; i < count; ++i) {
      // Filter on features if set
      if (feature_types.length && feature_types.includes(data[i].type) === false) {
        continue; // Skip item
      }
      // Create new feature
      let feature = new Feature({
        geometry: new Point(fromLonLat([data[i].lon, data[i].lat])),
        type: data[i].type,
      });
      // Set feature ID
      feature.setId(data[i].nid);
      // Add feature to array
      if (drupalSettings.node && data[i].nid == drupalSettings.node.nodeId) {
        console.log('add feature to top', data[i].nid, drupalSettings.node.nodeId)
        // Add feature to the top layer
        sourceTop.addFeature(feature);
      }
      else {
        features.push(feature);
      }
    }
    // Add features to the source
    source.addFeatures(features);
  }

  // Enable geolocation
  if (geolocation_enabled) {
    const geolocation = new Geolocation({
      trackingOptions: {
        enableHighAccuracy: true,
      },
      projection: view.getProjection(),
      tracking: true,
    });

    geolocation.on('change:position', function () {
      // Trigger geolocation only once, so the user can keep scrolling and panning after the geolcation is been set.
      if (geolocation_has_triggered === false) {
        const coordinates = geolocation.getPosition();
        view.setCenter(coordinates);
        view.setZoom(14)
        geolocation_has_triggered = true;
      }
    });
  }

  /**
   * Add a click handler to hide the popup.
   * @return {boolean} Don't follow the href.
   */
  if (popup_closer) {
    popup_closer.onclick = function () {
      overlay.setPosition(undefined);
      return false;
    };
  }

  /**
   * Returns the style for a single feature.
   *
   * @param feature
   * @returns {Style[]}
   */
  function featureStyle(feature) {
    let type = feature.get('type');
    let id = feature.getId();
    let icon = 'unknown.svg';
    let iconColor = '#000';

    switch (type) {
      case 'monument':
        icon = 'monument.svg';
        break;
      case 'cemetery':
        icon = 'cemetery.svg';
        break;
      case 'memorial':
        icon = 'memorial.svg';
        break;
      case 'museum':
        icon = 'museum.svg';
        break;
      case 'battle':
        icon = 'battle.svg';
        break;
      case 'operation':
        icon = 'operation.svg';
        break;
      case 'biography':
        icon = 'biography.svg';
        break;
    }

    // Default marker style
    let styleMarker = new Style({
      image: new CircleStyle({
        radius: 20,
        stroke: new Stroke({
          color: '#c5c5c5',
        }),
        fill: new Fill({
          color: '#fff',
        }),
      }),
    });
    // Override marker style when the feature is selected
    if (id === selected_feature_id) {
      styleMarker = new Style({
        image: new Icon({
          src: '/'+drupalSettings.path.themeUrl+'/img/map/marker-selected.png',
          scale: 0.5,
          displacement: [0, -8],
        }),
      });
      // Override icon color
      iconColor = '#fff';
    }

    let styleIcon = new Style({
      geometry: feature.getGeometry(),
      image: new Icon({
        src: '/'+drupalSettings.path.themeUrl+'/img/map/'+icon,
        color: iconColor,
      }),
    });

    return [styleMarker, styleIcon];
  }

  /**
   * Returns the style for cluster features.
   *
   * @param feature
   * @returns {Style}
   */
  function clusterStyle(feature) {
    const size = feature.get('features').length;
    let style = styleCache[size];
    if (!style) {
      if (size > 1) {
        style = new Style({
          image: new CircleStyle({
            radius: 20,
            stroke: new Stroke({
              color: '#fff',
            }),
            fill: new Fill({
              color: '#2e384f',
            }),
          }),
          text: new Text({
            text: size.toString(),
            fill: new Fill({
              color: '#fff',
            }),
          }),
        });
        styleCache[size] = style;
      }
      else {
        const originalFeature = feature.get('features')[0];
        return featureStyle(originalFeature);
      }
    }
    return style;
  }

  /**
   * Load an article popup HTML
   *
   * @param nid
   * @returns {string|*[]}
   */
  async function loadArticleHtml(nid) {
    const response = await fetch("/warchive/node-html/" + nid + "/teaser_distance");
    // Show error when there is something wrong
    if (!response.ok) {
      console.log("Features could not be loaded. Error: [" + response.status + "] " + response.statusText, {timeout: false});
      return [];
    }
    return await response.text();
  }

  // Return the instance
  return mapInstance;

}

